import React, { useContext, useRef } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import classNames from 'classnames';

import '../../../css/Griddable.scss';

import { LessonMode } from '../../../Constants';

import { register } from '../../../i18n';

import responseService from '../../services/ResponseService';

import useStyleEvents from '../../../hooks/useStyleEvents';

import FeedbackIcon from '../FeedbackIcon';
import Guideline from '../tools/Guideline';
import HtmlComponent from '../HtmlComponent';
import OptionalImage from '../OptionalImage';
import PrintQuestionNumber from './PrintQuestionNumber';

const t = register('AriaLabels');

const GRIDDABLE_SIGN_COLUMN_INDEX = -1; // Special index to distinguish the sign column (+/-) from digit columns (0, 1, 2, ...)
const GRIDDABLE_SMALL_STEP_SIZE = 1; // Number of positions to jump when using arrow keys
const GRIDDABLE_LARGE_STEP_SIZE = 5; // Number of positions to jump when using Page Up or Page Down
const GRIDDABLE_EMPTY_VALUE = ' '; // Empty cell representation

const Griddable = observer(({ lessonElementId }) => {
  const {
    lessonManager,
    responseManager,
    questionFeedbackManager,
    toolbarManager
  } = useContext(MobXProviderContext);

  const inputRowRef = useRef(null);
  const spinbuttonRefs = useRef({});

  const model = lessonManager.getSlideModel(lessonElementId);
  const lessonElementState = responseManager.getLessonElementState(lessonElementId);
  const feedbackState = questionFeedbackManager.getUiState(lessonElementId);

  const { currentResponseAnswer } = lessonElementState;
  const { readOnly } = lessonElementState;
  const { showAnswers, showAnswerFeedback } = feedbackState;

  // Define state
  const hasSignInput = model.hasSignInput ?? true; // Whether to allow the user to change between positive and negative
  const columnCount = model.columnCount ?? 6; // The total number of columns for number inputs including the decimal point
  const decimalType = model.decimalType ?? 'fixed'; // "fixed" means the publisher decided the location of the decimal while "floating" means the student can enter one anywhere
  const decimalColumn = model.decimalColumn ?? 1; // The right aligned column location of the decimal point in fixed mode. So 0 would mean the last column and 1 would mean one number can be entered after the decimal point

  const textBody = model.questionText ?? '';

  // Helper functions for getting an manipulating the answer
  const currentAnswer = currentResponseAnswer.text;
  const answerColumns = currentAnswer.split('');
  const sign = answerColumns[0];
  const answerNumber = answerColumns.slice(1);
  const correctAnswer = model.validation?.answers?.[0]?.text;

  useStyleEvents(lessonElementId);

  const getCurrentAnswer = () => {
    // The behavior has the data needed for this accessibility helper function.
    const behavior = lessonElementState.questionBehavior;
    const valid = behavior.checkForValidResponse(currentResponseAnswer);
    if (valid) {
      const fullNumber = behavior._toNumber(currentAnswer);
      if (!fullNumber) {
        return currentAnswer;
      } else {
        return `${fullNumber > 0 ? 'Positive' : 'Negative'} ${fullNumber}`;
      }
    } else {
      return t('incomplete');
    }
  };

  const getCorrectAnswer = () => {
    const behavior = lessonElementState.questionBehavior;
    // We assume the correct answer is valid
    const fullNumber = behavior._toNumber(correctAnswer);
    if (!fullNumber) {
      return correctAnswer;
    } else {
      return `${fullNumber > 0 ? 'Positive' : 'Negative'} ${Math.abs(fullNumber)}`;
    }
  };

  const getAnswerNumberDecimalColumn = () => {
    // The decimal column number is right aligned
    return columnCount - decimalColumn - 1;
  };

  const setSign = (sign) => {
    const possibleValues = getPossibleSignValues();
    // Allow valid sign values or empty value (for clearing via keyboard)
    if (possibleValues.includes(sign) || sign === GRIDDABLE_EMPTY_VALUE) {
      const newAnswer = [...answerColumns];
      newAnswer[0] = sign;
      responseService.responseChangeHandler(newAnswer.join(''), lessonElementId);
      // Focus the sign column spinbutton after bubble selection
      spinbuttonRefs.current[GRIDDABLE_SIGN_COLUMN_INDEX]?.focus();
    }
  };

  const getPossibleSignValues = () => {
    return ['+', '-'];
  };

  const setColumnValue = (columnIndex, value, { shouldToggle = false } = {}) => {
    const possibleValues = getPossibleValues();
    // The answer columns are offset by one because of the sign column
    const answerColumnIndex = columnIndex + 1;
    // Allow valid digit values or empty value (for clearing via keyboard)
    if (possibleValues.includes(value) || value === GRIDDABLE_EMPTY_VALUE) {
      const newAnswer = [...answerColumns];
      // TODO remove - Auto-removing decimals can cause unexpected behavior for the user.
      // TODO Since users are allowed to choose a decimal in every spinbutton column,
      // TODO users should be able to control exact decimal placement without programmatic interference.
      // if (value === '.') {
      //   // Then we need to remove all other decimal points and replace them with empty values
      //   newAnswer = newAnswer.map((columnValue) => (columnValue === '.' ? GRIDDABLE_EMPTY_VALUE : columnValue));
      // }
      newAnswer[answerColumnIndex] = value;
      // Toggle behavior only for bubble clicks:
      // if user clicked the same value again, remove it and replace with empty value
      if (shouldToggle && answerColumns[answerColumnIndex] === value) {
        newAnswer[answerColumnIndex] = GRIDDABLE_EMPTY_VALUE;
      }
      responseService.responseChangeHandler(newAnswer.join(''), lessonElementId);
      // Focus the digit column spinbutton after bubble selection
      if (shouldToggle) {
        spinbuttonRefs.current[columnIndex]?.focus();
      }
    }
  };

  const getPossibleValues = () => {
    const values = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    if (decimalType === 'floating') {
      values.unshift('.');
    }
    return values;
  };

  return (
    <div className='griddable-question'>
      {toolbarManager.isGuidelineOpen && <Guideline lessonElementId={lessonElementId} />}
      <div className='test-item-question griddable'>
        {(lessonManager.playerMode === LessonMode.PRINT_PREVIEW) && <PrintQuestionNumber model={model} />}
        <HtmlComponent htmlStr={textBody} />
      </div>
      <OptionalImage model={model} runtime={true} />
      <div className='test-item-answers'>
        {showAnswerFeedback && <div className='feedback-icon-container'><FeedbackIcon lessonElementId={lessonElementId} /></div>}
        <table className='griddable-table'>
          <tbody>
            <tr ref={inputRowRef} className='input-row'>
              {hasSignInput && (
                <EditableCell
                  ref={(element) => { spinbuttonRefs.current[GRIDDABLE_SIGN_COLUMN_INDEX] = element; }}
                  disabled={readOnly}
                  fullNumber={showAnswers ? getCorrectAnswer() : getCurrentAnswer()}
                  index={GRIDDABLE_SIGN_COLUMN_INDEX}
                  onChange={(value) => setSign(value)}
                  validCharacters={getPossibleSignValues()}
                  value={sign}
                />
              )}
              {
                answerNumber.map((value, index) => {
                  const isDecimalColumn = index === getAnswerNumberDecimalColumn();
                  const disabled = readOnly || (isDecimalColumn && decimalType === 'fixed'); // You cannot enter anything into the fixed decimal column
                  return (
                    <EditableCell
                      key={index}
                      ref={(element) => { spinbuttonRefs.current[index] = element; }}
                      disabled={disabled}
                      fullNumber={showAnswers ? getCorrectAnswer() : getCurrentAnswer()}
                      index={index}
                      onChange={(value) => setColumnValue(index, value)}
                      validCharacters={getPossibleValues()}
                      value={value}
                    />
                  );
                })
              }
            </tr>
            <tr className='bubble-row'>
              {hasSignInput && (
                <td className='bubble-column-cell'>
                  <BubbleColumn
                    disabled={readOnly}
                    onSelect={setSign}
                    selectedValue={sign}
                    values={getPossibleSignValues()}
                  />
                </td>
              )}
              {
                answerNumber.map((selectedValue, index) => {
                  const possibleValues = getPossibleValues();
                  const hasBubbles = decimalType === 'floating' || index !== getAnswerNumberDecimalColumn(); // If we are fixed and this is the decimal column, we don't need to show bubbles
                  return (
                    <td key={index} className='bubble-column-cell'>
                      <BubbleColumn
                        disabled={readOnly}
                        onSelect={(value) => setColumnValue(index, value, { shouldToggle: true })}
                        selectedValue={selectedValue}
                        values={possibleValues}
                        visible={hasBubbles}
                      />
                    </td>
                  );
                })
              }
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
});
export default Griddable;

function positiveMod(n, m) {
  return ((n % m) + m) % m;
}

/**
 * EditableCell Component - Spinbutton control for Griddable question type
 *
 * Render a focusable table cell that allows keyboard navigation through valid character options.
 * Use arrow keys to cycle through values, Page Up/Down for larger steps, and Home/End for boundary values.
 */
const EditableCell = React.forwardRef(({ value, onChange, validCharacters, index, fullNumber, directInput = false, arrowInput = true, disabled = false }, ref) => {
  const disable = disabled || (!directInput && !arrowInput); // If there is no possible input, we disable the cell

  const handleKeyPress = (event) => {
    const { key } = event;
    if (directInput && validCharacters.includes(key)) {
      event.preventDefault();
      event.stopPropagation();
      onChange(key);
    }

    if (arrowInput) {
      if (key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();
        const currentIndex = validCharacters.indexOf(value);
        // If nothing is selected, select the final value.
        // Otherwise, select the previous value or wrap around to the last value.
        const newIndex = currentIndex >= 0 ? positiveMod(
          currentIndex - GRIDDABLE_SMALL_STEP_SIZE, validCharacters.length
        ) : validCharacters.length - 1;
        onChange(validCharacters[newIndex]);
      } else if (key === 'ArrowDown') {
        event.preventDefault();
        event.stopPropagation();
        const currentIndex = validCharacters.indexOf(value);
        // If nothing is selected, select the first value.
        // Otherwise, select the next value or wrap around to the first value.
        const newIndex = currentIndex >= 0 ? positiveMod(
          currentIndex + GRIDDABLE_SMALL_STEP_SIZE, validCharacters.length
        ) : 0;
        onChange(validCharacters[newIndex]);
      } else if (key === 'PageUp') {
        event.preventDefault();
        event.stopPropagation();
        const currentIndex = validCharacters.indexOf(value);
        // Jump backward by GRIDDABLE_LARGE_STEP_SIZE positions, or to the beginning if less than GRIDDABLE_LARGE_STEP_SIZE from start
        const newIndex = currentIndex >= 0 ? Math.max(0, currentIndex - GRIDDABLE_LARGE_STEP_SIZE) : validCharacters.length - 1;
        onChange(validCharacters[newIndex]);
      } else if (key === 'PageDown') {
        event.preventDefault();
        event.stopPropagation();
        const currentIndex = validCharacters.indexOf(value);
        // Jump forward by GRIDDABLE_LARGE_STEP_SIZE positions, or to the end if less than GRIDDABLE_LARGE_STEP_SIZE from end
        const newIndex = currentIndex >= 0 ? Math.min(validCharacters.length - 1, currentIndex + GRIDDABLE_LARGE_STEP_SIZE) : 0;
        onChange(validCharacters[newIndex]);
      } else if (key === 'Home') {
        event.preventDefault();
        event.stopPropagation();
        // Jump to first valid character
        onChange(validCharacters[0]);
      } else if (key === 'End') {
        event.preventDefault();
        event.stopPropagation();
        // Jump to last valid character
        onChange(validCharacters[validCharacters.length - 1]);
      }
    }

    // Backspace or Delete clears the current spinbutton value
    if (key === 'Backspace' || key === 'Delete') {
      event.preventDefault();
      event.stopPropagation();
      onChange(GRIDDABLE_EMPTY_VALUE);
    }
  };

  let accessibleName = value;
  if (value === '+') {
    accessibleName = t('positive');
  } else if (value === '-') {
    accessibleName = t('negative');
  } else if (value === GRIDDABLE_EMPTY_VALUE) {
    accessibleName = t('empty');
  }

  // Get the current value index for aria-valuenow
  const currentIndex = validCharacters.indexOf(value);
  const valueNow = currentIndex >= 0 ? currentIndex : 0;

  // Construct label that describes the control and context
  // Comma added for screen reader pause between answer status and spinbutton role
  const controlLabel = `${t('currentAnswerLabel')} ${fullNumber},`;
  const valueText = `${t('currentColumnValue')} ${accessibleName}`;

  return (
    <td
      ref={ref}
      aria-label={controlLabel}
      aria-valuemax={validCharacters.length - 1}
      aria-valuemin={0}
      aria-valuenow={valueNow}
      aria-valuetext={valueText}
      className={classNames('input-cell', { editable: !disable })}
      onKeyDown={handleKeyPress}
      role='spinbutton'
      tabIndex={disable ? -1 : 0}
    >
      <div className='editable-cell-content-box'>{value}</div>
    </td>
  );
});

EditableCell.displayName = 'EditableCell';

const BubbleColumn = ({ values, onSelect, selectedValue, disabled, visible = true }) => {
  const takesInput = !disabled && visible;
  const handleClick = (value) => {
    if (takesInput) {
      onSelect(value);
    }
  };

  return (
    <div className='bubble-column'>
      {
        values.map((value, index) => {
          const selected = selectedValue === value;
          return (
            <div key={index} className='bubble-container' onClick={() => handleClick(value)} style={{ opacity: visible ? 1 : 0 }}>
              <div className={classNames('bubble', { selected, disabled: !takesInput })}>{value}</div>
            </div>
          );
        })
      }
    </div>
  );
};
